




















import { computed, defineComponent, ref } from "@vue/composition-api";
import { FileData, InputFileId } from "@scrile/api-provider/dist/api/FileProvider";
import { QuestionaryInfo } from "@scrile/api-provider/dist/api/QuestionaryInfoProvider";
import { TagInfo, TagInfoType } from "@scrile/api-provider/dist/api/TagsProvider";
import { InputUserUpdate } from "@scrile/api-provider/dist/api/UserProvider";
import { InputUserPublicInformationUpdate } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import {
  ContentVerification,
  ContentVerificationStatus,
  ContentVerificationSubjectType,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentVerificationProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useFiles from "@/hooks/useFiles";
import useProfileLinks from "@/hooks/useProfileLinks";
import useTags from "@/hooks/useTags";
import useUserTags from "@/hooks/useUserTags";
import Store from "@/store";
import { MUTATION_TYPES } from "@/store/const";
import providers from "@/lib/api";
import { getQuestionaryInfoFields, normalizedFields } from "@/lib/questionaryFieldsHelper";
import { getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { FormValues } from "@/types";
import PageProfileEditView from "./components/PageProfileEditView.vue";
import { IFileUploadingType, linkFields, ProfileFileType } from "./const";

export default defineComponent({
  name: "PageProfileEdit",
  components: { PageProfileEditView },
  props: {
    userId: { type: String, required: true },
  },
  setup(props, { root }) {
    const fetch = ref(true);
    const loading = ref(false);
    const info = ref<Record<string, string>>({});
    const files = ref<FileData[]>([]);
    const user = ref<UserPublicData | null>(null);
    const userTags = ref<string[]>([]);
    const fileLoading = ref<IFileUploadingType>({ loading: false });
    const avatarVerification = ref<ContentVerification | null>(null);
    const backgroundVerification = ref<ContentVerification | null>(null);

    const { authUserStrict } = useAuthUser();
    const { findFiles, uploadThenUpdate } = useFiles();
    const { userTagsGetData } = useUserTags();
    const { getTags, tags: systemTags } = useTags();
    const { profileLinks, getProfileLinks, updateProfileLinks } = useProfileLinks(computed(() => props.userId));

    const isAuthor = computed(() => authUserStrict.value.id === props.userId);
    const selectedTags = computed<TagInfo[]>(() => systemTags.value.filter(t => userTags.value.includes(t.id)));

    const getProfileContentVerification = async () => {
      const response = await providers.ContentVerificationProvider.search({
        data: {
          ownerId: props.userId,
          status: ContentVerificationStatus.NEW,
          subjectTypes: [ContentVerificationSubjectType.AVATAR, ContentVerificationSubjectType.BACKGROUND],
        },
      });
      avatarVerification.value =
        response.result.find(item => item.subject.subjectType === ContentVerificationSubjectType.AVATAR) ?? null;
      backgroundVerification.value =
        response.result.find(item => item.subject.subjectType === ContentVerificationSubjectType.BACKGROUND) ?? null;
    };

    const getFiles = async () => {
      files.value = await findFiles({
        fileIds: [
          {
            subjectId: props.userId,
            type: "avatar",
          },
          {
            subjectId: props.userId,
            type: "background_image",
          },
        ],
      });
    };

    const getUserData = async () => {
      const r = await providers.UserPublicDataProvider.search({ data: { id: [props.userId] } });
      user.value = r.result.find(i => i.id === props.userId) ?? null;
    };

    const getUserTags = async () => {
      if (!config.featureTags) return;
      userTags.value = await userTagsGetData();
    };

    const getSystemTags = async () => {
      if (!config.featureTags) return;
      await getTags(TagInfoType.USERS);
    };

    const getData = async () => {
      try {
        fetch.value = true;
        await Promise.all([
          providers.QuestionaryInfoProvider.find({ id: props.userId }).then(
            r => (info.value = r.fields as Record<string, string>)
          ),
          getUserData(),
          getProfileContentVerification(),
          getFiles(),
          getSystemTags(),
          getUserTags(),
          getProfileLinks(),
        ]);
      } finally {
        fetch.value = false;
      }
    };

    const updateFiles = async () => {
      await getProfileContentVerification();
      if (!avatarVerification.value && !backgroundVerification.value) {
        await getFiles();
        if (!user.value) return;

        const updatedUserPfp = {
          avatar: files.value.find(i => i.fileId.type === "avatar") ?? null,
          background: files.value.find(i => i.fileId.type === "background_image") ?? null,
        };
        if (isAuthor.value) {
          Store.commit(MUTATION_TYPES.SET_USER, {
            ...authUserStrict.value,
            ...updatedUserPfp,
          });
        }
        user.value = { ...user.value, ...updatedUserPfp };
      }
    };

    const deleteAvatar = async () => {
      await providers.FileProvider.delete({
        data: {
          subjectId: props.userId,
          type: avatarVerification.value ? ProfileFileType.AVATAR_VERIFICATION : ProfileFileType.AVATAR,
        },
      });
      await updateFiles();
    };
    const deleteBackground = async () => {
      await providers.FileProvider.delete({
        data: {
          subjectId: props.userId,
          type: backgroundVerification.value ? ProfileFileType.BACKGROUND_VERIFICATION : ProfileFileType.BACKGROUND,
        },
      });
      await updateFiles();
    };

    const updateUserTags = (tags: string[]) => {
      userTags.value = tags;
    };

    const upload = async (file: FileList, type: ProfileFileType, cb?: () => void) => {
      try {
        fileLoading.value = {
          type: type,
          loading: true,
        };
        if (file.length === 0) return;
        const fileId: InputFileId = {
          type,
          subjectId: props.userId,
        };
        await uploadThenUpdate({ fileId, file: file[0] });
        await updateFiles();
        cb && cb();
      } finally {
        fileLoading.value = { loading: false };
      }
    };

    const save = async (values: FormValues) => {
      const {
        screenName,
        username,
        localizationCountryCode,
        localizationStateCode,
        localizationCity,
        localizationLanguage,
        socialFields,
        ...questionaryInfoFields
      } = values;
      const userData: InputUserUpdate = {
        id: props.userId,
        screenName,
        username,
      };

      const userPublicInformation: InputUserPublicInformationUpdate = {
        userId: props.userId,
        location: {
          countryCode: localizationCountryCode || "",
          stateCode: localizationStateCode || "",
          city: localizationCity || "",
        },
        languages: localizationLanguage,
      };

      normalizedFields(getQuestionaryInfoFields(), questionaryInfoFields);
      normalizedFields(linkFields, socialFields);

      const questionaryInfo: QuestionaryInfo = {
        fields: {
          ...questionaryInfoFields,
          userId: props.userId,
        },
      };
      const promises: Promise<any>[] = [
        providers.QuestionaryInfoProvider.update({ data: questionaryInfo }),
        providers.UserProvider.update({ data: userData }).then(
          r => isAuthor.value && Store.commit(MUTATION_TYPES.SET_USER, r)
        ),
        updateProfileLinks(socialFields),
      ];
      if (!config.featureHideLocalization) {
        promises.push(providers.UserPublicDataProvider.userPublicInformationUpdate(userPublicInformation));
      }
      try {
        loading.value = true;
        await Promise.all(promises);
      } finally {
        loading.value = false;
      }
      root.$router.push(getProfileUrl(user.value ?? props.userId));
    };

    getData();

    return {
      fetch,
      loading,
      user,
      info,
      files,
      selectedTags,
      fileLoading,
      avatarVerification,
      backgroundVerification,
      profileLinks,
      updateUserTags,
      upload,
      save,
      deleteAvatar,
      deleteBackground,
    };
  },
});
